import React, { useRef, useEffect, useContext, useState } from 'react'
import { useRouter } from 'next/router'

import NavHeightContext from '@context/NavHeightContext'

import { useSession } from 'next-auth/react'

import menuData from '@data/menu.json'
import { MARKET_ENABLED } from '@lib/constants'
import { getHeaderContents } from '@lib/prismic/fetch/documents'

import Cart from '@components/cart/Cart'

import DesktopNav from './DesktopNav'
import MobileNav from './MobileNav'
import PromoBar from './PromoBar'

export default function Nav() {
	const { data: session } = useSession()
	const router = useRouter()
	const navWrapperRef = useRef<HTMLDivElement>(null)
	const navHeightContext = useContext(NavHeightContext)
	const isHomepage = router.pathname === '/'
	const [scrollPosition, setScrollPosition] = useState(0)
	const [headerContents, setHeaderContents] = useState<any>(null)

	if (!navHeightContext) {
		throw new Error('Nav must be used within a NavHeightContext.Provider')
	}

	const { setNavHeight } = navHeightContext

	// Track scroll position to show/hide promo bar and change nav background
	// Fetch header contents from Prismic
	useEffect(() => {
		getHeaderContents().then((data) => {
			if (data) {
				setHeaderContents(data.data)
			}
		})
	}, [])

	useEffect(() => {
		const handleScroll = () => {
			setScrollPosition(window.scrollY)
		}

		window.addEventListener('scroll', handleScroll)
		return () => window.removeEventListener('scroll', handleScroll)
	}, [])

	useEffect(() => {
		// Create a new ResizeObserver that will be called whenever the size of an observed element changes.
		const resizeObserver = new ResizeObserver((entries) => {
			// Loop through all observed elements that had their size changed.
			for (const entry of entries) {
				// If the navWrapperRef is currently referencing an element (i.e., it's not null),
				// update the navHeight state with the current height of the element.
				if (navWrapperRef.current) {
					setNavHeight(navWrapperRef.current.offsetHeight)
				}
			}
		})

		// Store the current ref value to use in cleanup
		const currentRef = navWrapperRef.current

		// If the navWrapperRef is currently referencing an element, start observing that element for size changes.
		if (currentRef) {
			resizeObserver.observe(currentRef)
		}

		// Return a cleanup function that will be called when the component unmounts or before the effect runs again.
		// This function stops observing the element for size changes.
		return () => {
			if (currentRef) {
				resizeObserver.unobserve(currentRef)
			}
		}
	}, [setNavHeight])

	// On homepage, show white background when promo bar is hidden (scrolled), otherwise transparent
	// On other pages, always show white background
	const shouldShowWhiteBackground = !isHomepage || scrollPosition > 50

	// Determine header background style
	const getHeaderBackgroundStyle = () => {
		if (headerContents?.header_background_color) {
			return {
				backgroundColor: headerContents.header_background_color,
				borderBottom: '1px solid rgba(229, 231, 235, 1)' // border-gray-200 equivalent
			}
		}
		return {}
	}

	const getHeaderBackgroundClass = () => {
		if (headerContents?.header_background_color) {
			return 'border-b border-gray-200'
		}
		// A11Y-KNOWN-ISSUE: Nav links on transparent background (homepage only, before scroll)
		// See ACCESSIBILITY_KNOWN_ISSUES.md for details
		return shouldShowWhiteBackground ? 'bg-white border-b border-gray-200' : 'bg-none border-none'
	}

	return (
		<div className="fixed inset-x-0 top-0 z-30" ref={navWrapperRef}>
			<header 
				className={`relative transition-colors duration-200 ${getHeaderBackgroundClass()}`}
				style={getHeaderBackgroundStyle()}
			>
				{/* Global Promo Bar Message */}
				<PromoBar />

				{/* Mobile */}
				<MobileNav
					menuData={MARKET_ENABLED ? menuData : { ...menuData, items: [] }}
					isLoggedIn={!!(session as any)?.shopifyAccessToken}
					headerContents={headerContents}
				/>

				{/* Desktop */}
				<DesktopNav
					menuData={MARKET_ENABLED ? menuData : { ...menuData, items: [] }}
					navWrapperRef={navWrapperRef}
					isLoggedIn={!!(session as any)?.shopifyAccessToken}
					headerContents={headerContents}
				/>
			</header>

			{/* Cart */}
			<Cart />
		</div>
	)
}
